import {Component, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor, FormBuilder, FormGroup,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors, Validators
} from "@angular/forms";
import {MAT_DATE_FORMATS} from "@angular/material/core";
import {MY_FORMATS} from "../form-field-next-date/form-field-next-date.component";
import {Observable, Subject, Subscription} from "rxjs";
import {debounce, debounceTime, filter, map, pairwise, startWith} from "rxjs/operators";
import {HunaValidators} from "../../providers/validators/HunaValidators";
import {isValidDate} from "../../providers/service/global-variables.service";

@Component({
    selector: 'app-form-field-range',
    templateUrl: './form-field-range.component.html',
    styleUrls: ['./form-field-range.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FormFieldRangeComponent
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: FormFieldRangeComponent
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: MY_FORMATS
        }
    ],
    standalone: false
})
export class FormFieldRangeComponent implements OnChanges, ControlValueAccessor {
    @Input() showSlider = true
    @Input() min: number | null = null
    @Input() max: number | undefined
    @Input() title: string | undefined
    @Input() step: number = 1
    @Input() initMin: number | undefined
    @Input() initMax: number | undefined
    @Input() changeOnlyOnceMin = true
    @Input() changeOnlyOnceMax = true
    @Output() emitValue = new Subject<{ minValue: number | undefined, maxValue: number | undefined }>()
    public formGroup: FormGroup;
    private subscriptions = new Subscription()
    minValue$: Observable<number | undefined>
    maxValue$: Observable<number | undefined>
    minInputMax$: Observable<number | null>
    maxInputMin$: Observable<number | null>
    values$: Observable<{ minValue: number | undefined, maxValue: number | undefined }>

    writingValue = false

    constructor(fb: FormBuilder) {
        this.formGroup = fb.group({
            minValue: [undefined, [HunaValidators.isNumberWithDecimal()]],
            maxValue: [undefined, [HunaValidators.isNumberWithDecimal()]],
        })
        this.minValue$ = this.formGroup.get('minValue')!.valueChanges.pipe(
            startWith(undefined),
            map(() => this.formGroup.get('minValue')?.value)
        )
        this.maxValue$ = this.formGroup.get('maxValue')!.valueChanges.pipe(
            startWith(undefined),
            map(() => this.formGroup.get('maxValue')?.value)
        )
        this.minInputMax$ = this.minValue$.pipe(
            map(min => {
                return this.minInputMax
            })
        )
        this.maxInputMin$ = this.maxValue$.pipe(
            map(max => {
                return this.maxInputMin
            })
        )

        this.values$ = this.formGroup.valueChanges.pipe(
            startWith(undefined),
            debounceTime(200),
            filter(values => {
                return !this.writingValue && this.setValid(values)
            }),
            pairwise(),
            filter(([prev, current]) => {
                if (prev == null || current == null) {
                    return true
                } else {
                    return prev.minValue !== current.minValue || prev.maxValue !== current.maxValue
                }
            }),
            map(([prev, current]) => current)
        )


        this.subscriptions.add(
            this.values$.subscribe(value => this.emitValue.next(value))
        );
    }

    get maxInputMin() {
        let maxMin = null
        const max = this.formGroup.get('maxValue')?.value
        if (max != null) {
            maxMin = max
            if (this.min != null && maxMin < this.min) {
                maxMin = this.min
            }
        }
        return maxMin
    }

    get minInputMax() {
        let minMax = null
        const min = this.formGroup.get('minValue')?.value
        if (min != null) {
            minMax = min
            if (this.max != null && minMax > this.max) {
                minMax = this.max
            }
        }

        return minMax
    }

    ngOnChanges(changes: SimpleChanges) {
        if ('initMin' in changes && this.changeOnlyOnceMin) {
            this.formGroup.get('minValue')?.setValue(this.initMin)
            this.changeOnlyOnceMin = false
        }
        if ('initMax' in changes && this.changeOnlyOnceMax) {
            this.formGroup.get('maxValue')?.setValue(this.initMax)
            this.changeOnlyOnceMax = false
        }
    }

    onTouched: Function = () => {
    };

    setValid(rangeValue: { minValue: number, maxValue: number }) {

        if (rangeValue != null) {
            const minValue = rangeValue.minValue
            const maxValue = rangeValue.maxValue
            const maxAccepted = this.maxInputMin
            const minAccepted = this.minInputMax
            if (minAccepted != null && maxAccepted != null) {
                const minIsValid = minValue == null || (minValue >= minAccepted && minValue <= maxAccepted);
                const maxIsValid = maxValue == null || (maxValue >= minAccepted && maxValue <= maxAccepted);
                if (!minIsValid) {
                    if (minValue > maxAccepted) {
                        // (this.formGroup as any).get('minValue').setValue(maxValue)
                    } else if (minValue < minAccepted) {
                        // (this.formGroup as any).get('minValue').setValue(minAccepted)
                    }
                }
                if (!maxIsValid) {
                    if (maxValue > maxAccepted) {
                        // (this.formGroup as any).get('maxValue').setValue(maxValue)
                    } else if (maxValue < minAccepted) {
                        // (this.formGroup as any).get('maxValue').setValue(minAccepted)
                    }
                }
                return minIsValid && maxIsValid
            } else if (minAccepted != null) {
                const minIsValid = minValue == null || minValue >= minAccepted;
                const maxIsValid = maxValue == null || maxValue >= minAccepted;
                if (!minIsValid) {
                    if (minValue < minAccepted) {
                        // (this.formGroup as any).get('minValue').setValue(minAccepted)
                    }
                }
                if (!maxIsValid) {
                    if (maxValue < minAccepted) {
                        // (this.formGroup as any).get('maxValue').setValue(minAccepted)
                    }
                }
                return minIsValid && maxIsValid
            } else if (maxAccepted != null) {
                const minIsValid = minValue == null || minValue <= maxAccepted;
                const maxIsValid = maxValue == null || maxValue <= maxAccepted;
                if (!minIsValid) {
                    if (minValue > maxAccepted) {
                        // (this.formGroup as any).get('minValue').setValue(maxValue)
                    }
                }
                if (!maxIsValid) {
                    if (maxValue > maxAccepted) {
                        // (this.formGroup as any).get('maxValue').setValue(maxValue)
                    }
                }
                return minIsValid && maxIsValid
            }
        }
        return true
    }

    registerOnChange(onChange: any) {
        const values$ = this.values$

        this.subscriptions.add(
            values$.subscribe(onChange)
        );


    }

    setDisabledState(disabled: boolean) {
        if (disabled) {
            this.formGroup.disable();
        } else {
            this.formGroup.enable();
        }
    }


    writeValue(values: any | undefined) {
        this.writingValue = true
        this.formGroup.reset()
        if (values) {
            if (isValidDate(values.minValue)) {
                this.formGroup.get('minValue')?.setValue(values.minValue)
            }
            if (isValidDate(values.maxValue)) {
                this.formGroup.get('maxValue')?.setValue(values.maxValue)
            }
            setTimeout(() => {
                this.writingValue = false
            })
        }

    }

    validate(control: AbstractControl): ValidationErrors | null {
        // if (!isNotNullOrUndefined(this.form.get('autoComplete')?.validator)) {
        //     this.form.get('autoComplete')?.setValidators(control.validator)
        //     this.form.get('autoComplete')?.updateValueAndValidity()
        // }
        if (this.formGroup.valid) {
            return null
        } else {
            return this.formGroup.errors!;
        }
    }


    registerOnTouched(onTouched: Function) {
        this.onTouched = onTouched;
    }

    unsetValue(key: string) {
        this.formGroup.get(key)?.reset()
    }
}
